import { Application, Container, Graphics } from 'pixi.js';
import { Player, Obstacle, Item, Road } from './entities';
import { useGameStore } from './store';
import { GAME_CONFIG, COLORS, OBSTACLE_TYPES, ITEM_TYPES } from './configs';
import type { ObstacleConfig } from './configs';
type ObstacleTypeKey = keyof typeof OBSTACLE_TYPES;
type ItemTypeKey = keyof typeof ITEM_TYPES;
export class Game {
  private app: Application;
  private gameContainer: Container;
  private player: Player;
  private road: Road;
  private obstacles: Obstacle[] = [];
  private items: Item[] = [];
  private obstacleTimer: number = 0;
  private itemTimer: number = 0;
  private scoreTimer: number = 0;
  private touchStartX: number = 0;
  private touchStartY: number = 0;
  private isInitialized: boolean = false;
  private destroyed: boolean = false;
  private boundUpdate: (ticker: { deltaTime: number }) => void;
  private stageWidth: number = 400;
  private stageHeight: number = 600;
  private resizeObserver: ResizeObserver | null = null;
  private windowKeydownHandler: ((e: KeyboardEvent) => void) | null = null;
  private windowKeyupHandler: ((e: KeyboardEvent) => void) | null = null;
  private canvasTouchStartHandler: ((e: TouchEvent) => void) | null = null;
  private canvasTouchMoveHandler: ((e: TouchEvent) => void) | null = null;
  private canvasTouchEndHandler: ((e: TouchEvent) => void) | null = null;
  constructor() {
    this.app = new Application();
    this.gameContainer = new Container();
    this.player = new Player();
    this.road = new Road();
    this.boundUpdate = (ticker) => this.update(ticker.deltaTime);
  }
  async init(canvas: HTMLCanvasElement): Promise<void> {
    if (this.destroyed) return;
    const container = canvas.parentElement;
    const rect = container?.getBoundingClientRect();
    const width = Math.max(280, Math.floor(rect?.width ?? 400));
    const height = Math.max(420, Math.floor(rect?.height ?? 600));
    this.stageWidth = width;
    this.stageHeight = height;
    await this.app.init({
      canvas,
      width,
      height,
      backgroundColor: COLORS.background,
      antialias: true,
      resolution: Math.min(window.devicePixelRatio || 1, 2),
      autoDensity: true,
    });
    if (this.destroyed) {
      try {
        this.app.destroy(true, { children: true, texture: true });
      } catch {
        /* ignore */
      }
      return;
    }
    this.buildScene();
    this.setupControls();
    // Add ticker AFTER everything is wired so first frames render entities
    this.app.ticker.add(this.boundUpdate);
    this.isInitialized = true;
    if (container) {
      this.resizeObserver = new ResizeObserver(() => {
        // CSS already resizes the canvas to fill wrapper; internal buffer stays
      });
      this.resizeObserver.observe(container);
    }
  }
  private buildScene(): void {
    // Sky band
    const sky = new Graphics();
    sky.rect(0, 0, this.stageWidth, Math.floor(this.stageHeight * 0.32));
    sky.fill(COLORS.sky);
    this.app.stage.addChild(sky);
    // Cityscape silhouette
    const cityscape = new Graphics();
    const baseY = Math.floor(this.stageHeight * 0.18);
    cityscape.rect(0, baseY, 60, 40).fill(0x0f0f1e);
    cityscape.rect(70, baseY - 20, 40, 60).fill(0x0f0f1e);
    cityscape.rect(120, baseY - 10, 50, 50).fill(0x0f0f1e);
    cityscape.rect(180, baseY - 30, 35, 70).fill(0x0f0f1e);
    cityscape.rect(230, baseY - 5, 55, 45).fill(0x0f0f1e);
    cityscape.rect(300, baseY - 15, 45, 55).fill(0x0f0f1e);
    cityscape.rect(355, baseY, 45, 40).fill(0x0f0f1e);
    this.app.stage.addChild(cityscape);
    // Stars
    const stars = new Graphics();
    for (let i = 0; i < 24; i++) {
      const sx = Math.floor(Math.random() * this.stageWidth);
      const sy = Math.floor(Math.random() * Math.floor(this.stageHeight * 0.22));
      const sr = Math.random() * 1.4 + 0.4;
      stars.circle(sx, sy, sr);
      stars.fill({ color: 0xfef3c7, alpha: 0.7 + Math.random() * 0.3 });
    }
    this.app.stage.addChild(stars);
    // Game container is centered horizontally so local lane offsets map to lanes
    this.gameContainer.x = this.stageWidth / 2;
    this.gameContainer.addChild(this.road);
    // CRITICAL: place player on-screen regardless of stageHeight
    this.player.x = (GAME_CONFIG.playerStartLane - 1) * GAME_CONFIG.laneWidth;
    this.player.y = this.stageHeight - 150;
    this.gameContainer.addChild(this.player);
    this.app.stage.addChild(this.gameContainer);
  }
  private setupControls(): void {
    this.windowKeydownHandler = (e) => {
      if (e.code === 'ArrowLeft' || e.code === 'KeyA') {
        this.player.moveLeft();
      }
      if (e.code === 'ArrowRight' || e.code === 'KeyD') {
        this.player.moveRight();
      }
      const store = useGameStore.getState();
      if (
        (e.code === 'ArrowUp' || e.code === 'KeyW' || e.code === 'Space') &&
        !store.isPlaying &&
        !store.isGameOver
      ) {
        store.startGame();
      }
      if (e.code === 'KeyP' || e.code === 'Escape') {
        if (store.isPlaying) {
          if (store.isPaused) {
            store.resumeGame();
          } else {
            store.pauseGame();
          }
        }
      }
    };
    this.windowKeyupHandler = () => {
      /* reserved for held-key states */
    };
    window.addEventListener('keydown', this.windowKeydownHandler);
    window.addEventListener('keyup', this.windowKeyupHandler);
    const canvas = this.app.canvas as HTMLCanvasElement;
    this.canvasTouchStartHandler = (e) => {
      e.preventDefault();
      if (e.touches.length > 0) {
        this.touchStartX = e.touches[0].clientX;
        this.touchStartY = e.touches[0].clientY;
      }
    };
    this.canvasTouchMoveHandler = (e) => {
      e.preventDefault();
    };
    this.canvasTouchEndHandler = (e) => {
      e.preventDefault();
      const touch = e.changedTouches[0];
      if (!touch) return;
      const dx = touch.clientX - this.touchStartX;
      const dy = touch.clientY - this.touchStartY;
      const absDx = Math.abs(dx);
      const absDy = Math.abs(dy);
      if (absDx > 30 && absDx > absDy) {
        if (dx > 0) {
          this.player.moveRight();
        } else {
          this.player.moveLeft();
        }
      }
    };
    canvas.addEventListener('touchstart', this.canvasTouchStartHandler, { passive: false });
    canvas.addEventListener('touchmove', this.canvasTouchMoveHandler, { passive: false });
    canvas.addEventListener('touchend', this.canvasTouchEndHandler, { passive: false });
  }
  update(delta: number): void {
    const store = useGameStore.getState();
    // Only simulate when actively playing; this is what advances score / spawns obstacles
    if (!store.isPlaying || store.isPaused) return;
    store.updatePowerUpTimers(delta);
    const speedMultiplier = store.powerUps.speed ? 2 : 1;
    this.player.update(delta);
    if (store.powerUps.fly !== this.player.getIsFlying()) {
      this.player.setFlying(store.powerUps.fly);
    }
    this.road.update(delta, store.speed * speedMultiplier);
    // Spawn obstacles (only when not flying)
    this.obstacleTimer += delta * 16.67;
    const obstacleInterval = GAME_CONFIG.obstacleSpawnInterval / Math.max(1, speedMultiplier);
    if (this.obstacleTimer > obstacleInterval) {
      this.spawnObstacle();
      this.obstacleTimer = 0;
    }
    // Spawn items
    this.itemTimer += delta * 16.67;
    if (this.itemTimer > GAME_CONFIG.itemSpawnInterval) {
      this.spawnItem();
      this.itemTimer = 0;
    }
    // Update obstacles
    for (let i = this.obstacles.length - 1; i >= 0; i--) {
      const obstacle = this.obstacles[i];
      obstacle.update(delta);
      obstacle.y += store.speed * speedMultiplier * delta;
      if (obstacle.y > this.stageHeight + 80) {
        if (obstacle.parent) this.gameContainer.removeChild(obstacle);
        obstacle.destroy();
        this.obstacles.splice(i, 1);
        continue;
      }
      if (!store.powerUps.fly && this.checkCollision(this.player.getBounds(), obstacle.getBounds())) {
        if (store.powerUps.shield) {
          this.player.deactivateShield();
          store.deactivatePowerUp('shield');
          this.player.flash(10);
          if (obstacle.parent) this.gameContainer.removeChild(obstacle);
          obstacle.destroy();
          this.obstacles.splice(i, 1);
        } else {
          this.player.flash(18);
          store.loseLife();
          if (obstacle.parent) this.gameContainer.removeChild(obstacle);
          obstacle.destroy();
          this.obstacles.splice(i, 1);
        }
      }
    }
    // Update items
    for (let i = this.items.length - 1; i >= 0; i--) {
      const item = this.items[i];
      item.update(delta);
      item.y += store.speed * speedMultiplier * delta;
      if (item.y > this.stageHeight + 80) {
        if (item.parent) this.gameContainer.removeChild(item);
        item.destroy();
        this.items.splice(i, 1);
        continue;
      }
      if (this.checkCollision(this.player.getBounds(), item.getBounds())) {
        const itemType = item.type as 'speed' | 'fly' | 'shield' | 'score';
        store.activatePowerUp(itemType);
        if (itemType === 'shield') {
          this.player.activateShield();
        }
        store.incrementScore(50);
        if (item.parent) this.gameContainer.removeChild(item);
        item.destroy();
        this.items.splice(i, 1);
      }
    }
    // Continuous distance score (so the HUD actually moves while playing)
    this.scoreTimer += delta * 16.67;
    if (this.scoreTimer > 100) {
      store.incrementScore(Math.max(1, Math.floor(store.speed)));
      this.scoreTimer = 0;
    }
    // Difficulty ramp
    if (store.score > 0 && store.score % 500 === 0) {
      store.increaseSpeed();
    }
  }
  private spawnObstacle(): void {
    const types: ObstacleTypeKey[] = ['pothole', 'barrier', 'cone'];
    const type = types[Math.floor(Math.random() * types.length)];
    const lane = Math.floor(Math.random() * GAME_CONFIG.lanes);
    const obstacle = new Obstacle(lane, type);
    const cfg: ObstacleConfig = OBSTACLE_TYPES[type];
    // Local coords - gameContainer.x = stageWidth/2 handles lane centering
    obstacle.x = (lane - 1) * GAME_CONFIG.laneWidth;
    obstacle.y = -cfg.height - 20;
    this.obstacles.push(obstacle);
    // CRITICAL: must be added to gameContainer so Pixi actually renders it
    this.gameContainer.addChild(obstacle);
  }
  private spawnItem(): void {
    const types: ItemTypeKey[] = ['speed', 'fly', 'shield', 'score'];
    const type = types[Math.floor(Math.random() * types.length)];
    const lane = Math.floor(Math.random() * GAME_CONFIG.lanes);
    const item = new Item(lane, type);
    item.x = (lane - 1) * GAME_CONFIG.laneWidth;
    item.y = -50;
    this.items.push(item);
    // CRITICAL: must be added to gameContainer so Pixi actually renders it
    this.gameContainer.addChild(item);
  }
  private checkCollision(
    a: { x: number; y: number; width: number; height: number },
    b: { x: number; y: number; width: number; height: number }
  ): boolean {
    return (
      a.x < b.x + b.width &&
      a.x + a.width > b.x &&
      a.y < b.y + b.height &&
      a.y + a.height > b.y
    );
  }
  reset(): void {
    for (const obstacle of this.obstacles) {
      if (obstacle.parent) this.gameContainer.removeChild(obstacle);
      obstacle.destroy();
    }
    this.obstacles = [];
    for (const item of this.items) {
      if (item.parent) this.gameContainer.removeChild(item);
      item.destroy();
    }
    this.items = [];
    this.player.deactivateShield();
    this.player.setFlying(false);
    this.player.x = (GAME_CONFIG.playerStartLane - 1) * GAME_CONFIG.laneWidth;
    this.player.y = this.stageHeight - 150;
    this.obstacleTimer = 0;
    this.itemTimer = 0;
    this.scoreTimer = 0;
  }
  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    if (this.windowKeydownHandler) {
      window.removeEventListener('keydown', this.windowKeydownHandler);
      this.windowKeydownHandler = null;
    }
    if (this.windowKeyupHandler) {
      window.removeEventListener('keyup', this.windowKeyupHandler);
      this.windowKeyupHandler = null;
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    if (this.isInitialized && this.app) {
      try {
        this.app.ticker.remove(this.boundUpdate);
      } catch {
        /* ticker may already be torn down */
      }
      try {
        const canvas = this.app.canvas as HTMLCanvasElement | undefined;
        if (canvas) {
          if (this.canvasTouchStartHandler) {
            canvas.removeEventListener('touchstart', this.canvasTouchStartHandler);
          }
          if (this.canvasTouchMoveHandler) {
            canvas.removeEventListener('touchmove', this.canvasTouchMoveHandler);
          }
          if (this.canvasTouchEndHandler) {
            canvas.removeEventListener('touchend', this.canvasTouchEndHandler);
          }
          if (canvas.parentNode) {
            canvas.parentNode.removeChild(canvas);
          }
        }
      } catch {
        /* ignore */
      }
      try {
        const renderer = this.app.renderer;
        if (renderer && renderer.events) {
          renderer.events.setTargetElement(null);
        }
      } catch {
        /* ignore */
      }
      try {
        this.app.destroy(true, { children: true, texture: true });
      } catch {
        /* ignore double-destroy */
      }
    }
  }
  get isReady(): boolean {
    return this.isInitialized;
  }
}